import React from 'react';
import { Gem } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useMockAuth } from '../contexts/MockAuthContext';
import { useNotificationContext } from './notification-provider';

interface StoreModalProps {
    isOpen: boolean;
    onClose: () => void;
    onPurchase?: () => void;
}

export default function StoreModal({ isOpen, onClose, onPurchase }: StoreModalProps) {
    const { updateGems } = useMockAuth();
    const { showSuccess } = useNotificationContext();
    const t = useTranslations('store');

    if (!isOpen) return null;

    const gemPacks = [
        { amount: 12, price: 0.99, image: '/images/gems/gem-1.png' }, // Placeholder images, I'll use Gem icon
        { amount: 60, price: 4.99, image: '/images/gems/gem-2.png', recommend: true },
        { amount: 180, price: 14.99, image: '/images/gems/gem-3.png' },
        { amount: 360, price: 29.99, image: '/images/gems/gem-4.png' },
        { amount: 600, price: 49.99, image: '/images/gems/gem-5.png' },
        { amount: 1200, price: 99.99, image: '/images/gems/gem-6.png' },
    ];

    const handleBuy = (amount: number) => {
        const successMessage = t.rich('successMessage', {
            amount,
            gem: () => (
                <span className="inline-flex items-center">
                    <Gem className="size-4 text-purple-600 fill-purple-600/20" aria-hidden="true" />
                </span>
            ),
        });
        updateGems(amount);
        showSuccess(t('successTitle'), successMessage, 3000);
        onPurchase?.();
    };

    const displayedPacks = gemPacks.slice(0, 4);

    return (
        <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="relative w-full max-w-md sm:max-w-sm bg-white border-t border-gray-100 sm:border sm:rounded-3xl rounded-t-3xl shadow-2xl animate-in slide-in-from-bottom duration-300 sm:zoom-in-95 flex flex-col">

                {/* Header */}
                <div className="flex items-center justify-center py-3 border-b border-gray-100">
                    <h2 className="text-lg font-bold text-gray-900 tracking-wide">{t('title')}</h2>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                    {displayedPacks.map((pack) => (
                        <div key={pack.amount} className="flex items-center justify-between p-3 bg-white hover:bg-gray-50 transition-colors rounded-xl border border-gray-100 shadow-sm relative group">
                            {pack.recommend && (
                                <div className="absolute top-0 right-0 bg-gradient-to-r from-red-500 to-pink-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-bl-lg rounded-tr-lg shadow-sm">
                                    {t('hot')}
                                </div>
                            )}

                            <div className="flex items-center gap-3">
                                {/* Simulated Image with Icon */}
                                <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center border border-gray-100 shadow-inner">
                                    <Gem className="w-5 h-5 text-purple-600 fill-purple-600/10" />
                                </div>

                                <div className="flex flex-col">
                                    <span className="font-bold text-gray-900 text-base">{pack.amount} <span className="text-purple-600 text-xs font-normal">{t('unit')}</span></span>
                                    <span className="text-gray-500 text-xs">US${pack.price}</span>
                                </div>
                            </div>

                            <button
                                onClick={() => handleBuy(pack.amount)}
                                className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 active:scale-95 transition-all text-white font-bold py-1.5 px-4 rounded-full text-xs shadow-lg shadow-cyan-900/20"
                            >
                                {t('buy')}
                            </button>
                        </div>
                    ))}
                </div>

                {/* Footer / Back Button */}
                <div className="p-3 border-t border-gray-100 bg-white pb-6 sm:pb-3 rounded-b-3xl">
                    <button
                        onClick={onClose}
                        className="w-full text-center text-gray-400 font-medium text-base hover:text-gray-600 transition-colors"
                    >
                        {t('cancel')}
                    </button>
                </div>

            </div>
        </div>
    );
}
